import React, { useState, useEffect } from "react"
import { ArrowRight, Search } from "lucide-react"

const CATEGORIES = ["All", "Design Theory", "Materials", "Interior", "Urbanism", "Technology", "Practice"]

function useArticles() {
  const [articles, setArticles] = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)

  useEffect(() => {
    fetch("/api/articles")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load articles")
        return r.json()
      })
      .then((data) => { setArticles(data); setLoading(false) })
      .catch((e) => { setError(e.message); setLoading(false) })
  }, [])

  return { articles, loading, error }
}

export default function Journal() {
  const { articles, loading, error } = useArticles()
  const [activeCategory, setActiveCategory] = useState("All")
  const [search, setSearch] = useState("")

  const filtered = articles.filter((a) => {
    const matchCat    = activeCategory === "All" || a.category === activeCategory
    const matchSearch =
      search === "" ||
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.category.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
  })

  const featured = filtered.find((a) => a.featured)
  const rest     = filtered.filter((a) => !a.featured)

  return (
    <main className="pt-24 min-h-screen bg-stone-950">
      {/* Header */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
          <span className="w-6 h-px bg-amber-400" />
          Ideas & Writing
        </span>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-100 leading-tight">
            Journal
          </h1>
          {/* Search */}
          <div className="relative w-full lg:w-72">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-600" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full bg-stone-900 border border-stone-800 text-stone-300 placeholder-stone-600 pl-10 pr-4 py-3 font-['Inter'] text-sm focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mt-8">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-['Space_Grotesk'] text-[10px] tracking-[0.2em] px-4 py-2 border transition-all duration-200 ${
                activeCategory === cat
                  ? "bg-amber-400 border-amber-400 text-stone-950"
                  : "border-stone-700 text-stone-500 hover:border-stone-500 hover:text-stone-300"
              }`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 pb-24">
        {/* Loading */}
        {loading && (
          <div className="text-center py-24">
            <p className="font-['Cormorant_Garamond'] text-3xl text-stone-600">Loading...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-24">
            <p className="font-['Inter'] text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Featured Article */}
        {!loading && !error && featured && (
          <article className="group cursor-pointer mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-stone-800 hover:border-stone-700 transition-colors">
              <div className="overflow-hidden h-80 lg:h-96">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-between bg-stone-900">
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-amber-400 uppercase">
                      {featured.category}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-stone-600" />
                    <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] bg-stone-800 text-stone-400 px-2 py-0.5">
                      FEATURED
                    </span>
                  </div>
                  <h2 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-light text-stone-100 leading-tight mb-5 group-hover:text-amber-200 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-8">
                    {featured.excerpt}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-['Inter'] text-xs text-stone-500">{featured.published_at}</p>
                    {featured.read_time && (
                      <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-600 uppercase">{featured.read_time} read</p>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-amber-400 group-hover:gap-3 transition-all">
                    READ <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </div>
          </article>
        )}

        {/* Article Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {rest.map((article) => (
              <article key={article.id} className="group cursor-pointer">
                <div className="overflow-hidden h-52 mb-5">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.25em] text-amber-400 uppercase">
                    {article.category}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-stone-600" />
                  <span className="font-['Inter'] text-xs text-stone-500">{article.published_at}</span>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100 leading-tight mb-3 group-hover:text-amber-200 transition-colors">
                  {article.title}
                </h3>
                <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-5">{article.excerpt}</p>
                <div className="flex items-center justify-between">
                  {article.read_time && (
                    <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-600 uppercase">
                      {article.read_time} read
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-stone-500 group-hover:text-amber-400 transition-colors ml-auto">
                    READ <ArrowRight size={12} />
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-24">
            <p className="font-['Cormorant_Garamond'] text-3xl text-stone-600">No articles found</p>
          </div>
        )}
      </div>
    </main>
  )
}
